import { useEmail } from '../hooks/useEmail';
import { Email } from '../types/email';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper,
  useMediaQuery,
  Card
} from '@mui/material';
import { styled, alpha, useTheme } from '@mui/material/styles';

interface EmailListProps {
  onSelectEmail?: (email: Email) => void;
  emptyMessage?: string;
  currentInboxEmail?: string;
}

// Styled components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.background.paper, 0.4),
  },
  '&.unread': {
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  }
}));

// Updated styled container for mobile view - more clean and minimal
const EmailListMobile = styled(Box)(({  }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px', // Increased gap between items
  width: '100%',
  margin: '0 auto',
  padding: '8px 4px', // Reduced padding
  boxSizing: 'border-box',
  maxWidth: '100%',
  overflowX: 'hidden'
}));

// Updated EmailCard with improved spacing and layout
const EmailCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2, 2.5), // More horizontal padding
  cursor: 'pointer',
  borderRadius: '10px',
  transition: 'transform 0.15s ease, box-shadow 0.2s ease',
  backgroundColor: alpha(theme.palette.background.paper, 0.3),
  border: '1px solid rgba(255, 255, 255, 0.04)', // Subtle border
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
  },
  '&.unread': {
    backgroundColor: alpha(theme.palette.background.paper, 0.5),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
  }
}));

// Updated card header with better spacing
const EmailCardHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center', // Center align the items
  marginBottom: theme.spacing(1.5), // More space before subject
}));

// Updated From styling
const EmailFrom = styled(Typography)(({ theme }) => ({
  fontWeight: 700, // Bolder text for sender
  maxWidth: '70%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  fontSize: '0.95rem', // Slightly larger
  color: theme.palette.mode === 'dark' ? '#fff' : theme.palette.text.primary,
}));

// Updated Date styling
const EmailDate = styled(Typography)(({ theme }) => ({
  color: alpha(theme.palette.text.secondary, 0.9),
  fontSize: '0.8rem',
  fontWeight: 400,
}));

// Updated Subject styling with more emphasis on unread
const EmailSubject = styled(Box)(({  }) => ({
  fontSize: '0.95rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: 'flex',
  alignItems: 'center',
  gap: '6px', // Space between dot and text
}));

// Make unread dot more prominent
const UnreadDot = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '1.2rem',
  fontWeight: 'bold',
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const EmptyState = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  textAlign: 'center',
  color: theme.palette.text.secondary,
}));

// Styled container for desktop view
const EmailListDesktop = styled(Box)(({ }) => ({
  width: '100%',
  overflowX: 'auto',
}));

function EmailList({ 
  onSelectEmail, 
  emptyMessage = "No messages in this mailbox",
  currentInboxEmail
}: EmailListProps) {
  const { emails } = useEmail();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  if (emails.length === 0) {
    return (
      <EmptyState>
        <Typography variant="body1" gutterBottom>{emptyMessage}</Typography>
        {currentInboxEmail && (
          <Typography variant="body2">
            Messages sent to {currentInboxEmail} will appear here
          </Typography>
        )}
      </EmptyState>
    );
  }
  
  // Mobile card layout
  if (isMobile) {
    return (
      <EmailListMobile>
        {emails.map((email) => {
          const dateDisplay = new Date(email.date).toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
          });
          
          return (
            <EmailCard
              key={email.id}
              className={!email.read ? 'unread' : ''}
              onClick={() => onSelectEmail && onSelectEmail(email)}
            >
              <EmailCardHeader>
                <EmailFrom>{email.from}</EmailFrom>
                <EmailDate>{dateDisplay}</EmailDate>
              </EmailCardHeader>
              <EmailSubject>
                {!email.read && <UnreadDot>•</UnreadDot>}
                <Typography 
                  noWrap 
                  sx={{ 
                    fontWeight: !email.read ? 500 : 400,
                    color: !email.read ? 'text.primary' : 'text.secondary',
                    flex: 1
                  }}
                >
                  {email.subject || '(No subject)'}
                </Typography>
              </EmailSubject>
            </EmailCard>
          );
        })}
      </EmailListMobile>
    );
  }
  
  // Desktop table layout
  return (
    <EmailListDesktop>
      <TableContainer component={Paper} sx={{ 
        bgcolor: 'transparent', 
        boxShadow: 'none',
        maxHeight: '60vh',
        overflow: 'auto',
        width: '100%'
      }}>
        <Table stickyHeader sx={{ width: '100%', minWidth: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: '40px' }}></TableCell>
              <TableCell sx={{ width: '25%' }}>From</TableCell>
              <TableCell sx={{ width: '45%' }}>Subject</TableCell>
              <TableCell sx={{ width: '25%' }}>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {emails.map((email) => {
              const dateDisplay = new Date(email.date).toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit'
              });
              
              return (
                <StyledTableRow
                  key={email.id}
                  className={!email.read ? 'unread' : ''}
                  onClick={() => onSelectEmail && onSelectEmail(email)}
                >
                  <TableCell padding="checkbox" sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    {!email.read && <UnreadDot>•</UnreadDot>}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Typography noWrap title={email.from}>
                      {email.from}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Typography noWrap title={email.subject}>
                      {email.subject}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {dateDisplay}
                    </Typography>
                  </TableCell>
                </StyledTableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </EmailListDesktop>
  );
}

export default EmailList;